import { atom, useRecoilState, selector, useRecoilValue } from 'recoil';
import { useCallback } from 'react';
import { readPurposeAPI, updatePurposeAPI, removePurposeAPI, UpdateUserPurposeParams } from '@/Data/User/Purpose';
import { UserPurpose } from 'ai-api/types/purpose';
import { randomString } from '@/Functions';

// Recoil atom for storing user purpose data
export const purposeState = atom<UserPurpose | null>({
    key: 'purpose'+randomString(4),
    default: null
});

// Selector for checking if user has a purpose set
export const hasPurposeSelector = selector({
    key: 'hasPurpose'+randomString(4),
    get: ({ get }) => {
        const purpose = get(purposeState);
        return !!purpose;
    }
});

// Hook for accessing and managing user purpose
export const usePurpose = () => {
    const [purpose, setPurpose] = useRecoilState(purposeState);
    const hasPurpose = useRecoilValue(hasPurposeSelector);

    // Fetch user purpose from API
    const syncPurpose = useCallback(async () => {
        try {
            const result = await readPurposeAPI();
            setPurpose(result);
            return result;
        } catch (error) {
            console.error('Error fetching purpose:', error);
            return null;
        }
    }, [setPurpose]);

    // Update user purpose
    const updatePurpose = useCallback(async (params: UpdateUserPurposeParams) => {
        try {
            const result = await updatePurposeAPI(params);
            if (!(result instanceof Error)) {
                setPurpose(result);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating purpose:', error);
            return false;
        }
    }, [setPurpose]);

    // Remove user purpose
    const removePurpose = useCallback(async () => {
        try {
            const success = await removePurposeAPI();
            if (success) {
                setPurpose(null);
            }
            return success;
        } catch (error) {
            console.error('Error removing purpose:', error);
            return false;
        }
    }, [setPurpose]);

    return {
        purpose,
        hasPurpose,
        syncPurpose,
        updatePurpose,
        removePurpose
    };
};